import { useEffect } from "react";
import "@/App.css";
import { Analytics } from "@vercel/analytics/react";
import { SpeedInsights } from "@vercel/speed-insights/react";
import { ArrowLeft, Home } from "lucide-react";
import {
  BrowserRouter,
  Link,
  Navigate,
  Routes,
  Route,
  useLocation,
  useParams,
} from "react-router-dom";
import HeroSection from "./components/HeroSection";
import LivingCodex from "./components/LivingCodex";
import ChapterSection from "./components/ChapterSection";
import SoundToggle from "./components/SoundToggle";
import BookLanding from "./components/BookLanding";
import MainLanding from "./components/MainLanding";
import AuthorPage from "./components/AuthorPage";
import LoreVaultPage from "./components/LoreVaultPage";
import OathboundLanding from "./components/OathboundLanding";
import PurchasePage from "./components/PurchasePage/PurchasePage";
import PurchaseReceiptPage from "./components/PurchaseReceipt/PurchaseReceiptPage";
import PurchaseVerification from "./components/PurchaseVerification/PurchaseVerification";
import ReviewPage from "./components/ReviewPage/ReviewPage";
import AdminConsole from "./components/AdminConsole/AdminConsole";
import { soundManager } from "./utils/soundManager";
import landingData from "./data/landing.json";
import chaptersData from "./data/chapters.json";
import trailerData from "./data/trailer.json";
import codexData from "./data/codex.json";
import bookData from "./data/book.json";
import authorData from "./data/author.json";
import loreVaultData from "./data/loreVault.json";
import { validateStaticContent } from "./utils/staticContentValidation";

const HERO_DATA = {
  title: "The Oath Of The Unhuan",
  title_image_url:
    "/media/images/title_8fe911d6-4c89-4b75-a2f0-c0748889c74d.png",
  hero_video_url: "/media/videos/hero_2b6a2cb0-c29e-4688-8696-5606992621fa.mp4",
  trailer_url: trailerData.trailer_url,
  background_video_url: "",
};

const SITE_URL = "https://pehuan.com";
const SITE_TITLE = "The Oath of the Unhuan";
const SEO_TITLE = "The Oath of the Unhuan | Book One of the Pehuan Saga";
const AUTHOR_NAME = "Mahesh Thakur";
const DEFAULT_DESCRIPTION =
  "Read the novel. Explore the lore. Enter the realm. Start with The Oath of the Unhuan, Book One of the Pehuan Saga, journey beyond the story into the world of the ancient Pehuans.";
const DEFAULT_IMAGE = `${SITE_URL}/media/images/preview.JPEG`;
const DEFAULT_IMAGE_ALT = "The Oath of the Unhuan preview artwork";
const RECEIPT_PREVIEW_OUTCOMES =
  process.env.NODE_ENV === "development"
    ? {
        success: {
          status: "success",
          receipt: {
            trackingNumber: "1042",
            paymentReference: "pay_preview_7K4M2Q",
            sku: "base_hard_cover",
            quantity: 1,
            unitPricePaise: 69_900,
            shippingAmountPaise: 5_000,
            totalAmountPaise: 74_900,
            currency: "INR",
            discountCode: null,
            paidAt: "2026-07-31T12:00:00.000Z",
          },
        },
        failure: {
          status: "failure",
          failure: {
            providerOrderId: "order_preview_8H3D5P",
            paymentReference: "pay_preview_2T7R9N",
          },
        },
      }
    : null;
const LOCAL_ADMIN_ENABLED =
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_ENABLE_LOCAL_ADMIN === "true";

const ROUTE_SEO = {
  "/": {
    title: SEO_TITLE,
    description: DEFAULT_DESCRIPTION,
  },
  "/app": {
    title: `${SITE_TITLE} | Immersive Application`,
    description:
      "Explore The Oath of the Unhuan immersive application: cinematic fantasy chapters, living codex entries, characters, places, and lore by Mahesh Thakur.",
  },
  "/app/codex": {
    title: `${SITE_TITLE} | Living Codex`,
    description:
      "Browse the living codex for The Oath of the Unhuan, including Pehuans, Unhuans, characters, locations, artifacts, and lore from Mahesh Thakur’s fantasy world.",
  },
  "/author": {
    title: `About ${AUTHOR_NAME} | ${SITE_TITLE}`,
    description:
      "Read a note from Mahesh Thakur, author and creator of The Oath of the Unhuan and the world of the Pehuans.",
  },
  "/purchase": {
    title: `Purchase ${SITE_TITLE}`,
    description:
      "Enter delivery details to purchase The Oath of the Unhuan premium hardcover edition.",
    robots: "noindex,follow",
  },
  "/purchase/receipt": {
    title: `Order Receipt | ${SITE_TITLE}`,
    description: `Order confirmation for ${SITE_TITLE}.`,
    robots: "noindex,nofollow,noarchive",
  },
  "/review": {
    title: `Submit a Review | ${SITE_TITLE}`,
    description: `Private reader review invitation for ${SITE_TITLE}.`,
    robots: "noindex,nofollow,noarchive",
  },
  "/admin": {
    title: `Admin Console | ${SITE_TITLE}`,
    description: `Private order administration for ${SITE_TITLE}.`,
    robots: "noindex,nofollow,noarchive",
  },
  "/lore_vault": {
    title: `Lore Vault | ${SITE_TITLE}`,
    description:
      "Open the Lore Vault for The Oath of the Unhuan: key Pehuan lore, sacred places, artifacts, elemental forces, and short explanations from the world of the saga.",
  },
  "/archive": {
    title: `Archive | ${SITE_TITLE}`,
    description:
      "Explore the archived gateway to The Oath of the Unhuan and the mythic world of the Pehuans.",
    robots: "noindex,follow",
  },
};

function setMetaAttribute(attribute, key, content) {
  if (!content) return;

  let tag = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setCanonical(href) {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    document.head.appendChild(link);
  }
  link.setAttribute("href", href);
}

function setStructuredData(data) {
  const id = "route-structured-data";
  let script = document.getElementById(id);
  if (!script) {
    script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    document.head.appendChild(script);
  }
  script.textContent = JSON.stringify(data);
}

function getSeoForPath(pathname) {
  if (pathname === "/book" || pathname.startsWith("/book/")) {
    return {
      title: `${SITE_TITLE} | Companion`,
      description: "Private companion material for The Oath of the Unhuan.",
      robots: "noindex,nofollow,noarchive",
      canonicalPath: "/book",
    };
  }

  if (pathname.startsWith("/app/chapter/")) {
    const chapterNumber = pathname.split("/").filter(Boolean).pop();
    const chapter = chaptersData.find(
      (item) => String(item.chapter_number) === String(chapterNumber),
    );

    if (chapter) {
      return {
        title: `${chapter.title} | ${SITE_TITLE}`,
        description: `${chapter.description.slice(0, 150).trim()}...`,
        canonicalPath: pathname,
      };
    }
  }

  return {
    ...(ROUTE_SEO[pathname] || {
      title: `Page Not Found | ${SITE_TITLE}`,
      description: `The requested page could not be found. Return to ${SITE_TITLE}, the immersive fantasy world by ${AUTHOR_NAME}.`,
      robots: "noindex,follow",
      canonicalPath: "/",
    }),
    canonicalPath: ROUTE_SEO[pathname] ? pathname : "/",
  };
}

function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = getSeoForPath(pathname);
    const title = seo.title || `${SITE_TITLE} by ${AUTHOR_NAME}`;
    const description = seo.description || DEFAULT_DESCRIPTION;
    const canonicalUrl = `${SITE_URL}${seo.canonicalPath || pathname}`;
    const robots = seo.robots || "index,follow,max-image-preview:large";

    document.title = title;
    setMetaAttribute("name", "description", description);
    setMetaAttribute("name", "author", AUTHOR_NAME);
    setMetaAttribute("name", "robots", robots);
    setMetaAttribute("name", "googlebot", robots);
    setMetaAttribute("property", "og:title", title);
    setMetaAttribute("property", "og:description", description);
    setMetaAttribute("property", "og:image", DEFAULT_IMAGE);
    setMetaAttribute("property", "og:image:alt", DEFAULT_IMAGE_ALT);
    setMetaAttribute("property", "og:image:width", "1672");
    setMetaAttribute("property", "og:image:height", "941");
    setMetaAttribute("property", "og:url", canonicalUrl);
    setMetaAttribute("property", "og:type", "website");
    setMetaAttribute("property", "og:site_name", SITE_TITLE);
    setMetaAttribute("name", "twitter:card", "summary_large_image");
    setMetaAttribute("name", "twitter:title", title);
    setMetaAttribute("name", "twitter:description", description);
    setMetaAttribute("name", "twitter:image", DEFAULT_IMAGE);
    setMetaAttribute("name", "twitter:image:alt", DEFAULT_IMAGE_ALT);
    setCanonical(canonicalUrl);

    if (pathname === "/book" || pathname.startsWith("/book/")) {
      setStructuredData({
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: `${SITE_TITLE} Companion`,
        isPartOf: { "@type": "WebSite", name: SITE_TITLE, url: SITE_URL },
        author: { "@type": "Person", name: AUTHOR_NAME },
      });
      return;
    }

    setStructuredData({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Person",
          "@id": `${SITE_URL}/#mahesh-thakur`,
          name: AUTHOR_NAME,
        },
        {
          "@type": "Book",
          "@id": `${SITE_URL}/#book`,
          name: SITE_TITLE,
          isPartOf: "Pehuan Saga",
          author: { "@id": `${SITE_URL}/#mahesh-thakur` },
          genre: ["Fantasy", "Cinematic fantasy", "Mythic fiction"],
          image: DEFAULT_IMAGE,
          url: SITE_URL,
          description: DEFAULT_DESCRIPTION,
        },
        {
          "@type": "WebSite",
          "@id": `${SITE_URL}/#website`,
          name: SITE_TITLE,
          url: SITE_URL,
          author: { "@id": `${SITE_URL}/#mahesh-thakur` },
          about: { "@id": `${SITE_URL}/#book` },
          description: DEFAULT_DESCRIPTION,
        },
      ],
    });
  }, [pathname]);

  return null;
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

function BackHomeButton() {
  return (
    <Link
      to="/app"
      className="fixed top-5 left-5 z-50 inline-flex items-center gap-2 border border-[#C6A85A]/60 bg-black/60 px-4 py-3 text-sm uppercase tracking-widest text-[#E0C878] transition-all duration-300 hover:border-[#E0C878] hover:bg-[#E0C878] hover:text-black"
      style={{ fontFamily: "Cinzel, serif", backdropFilter: "blur(10px)" }}
      data-testid="back-home-button"
    >
      <ArrowLeft className="h-4 w-4" />
      Back
    </Link>
  );
}

function NotFoundPage() {
  return (
    <main className="not-found-page">
      <section className="not-found-panel" aria-labelledby="not-found-title">
        <p className="not-found-kicker">Lost Path</p>
        <h1 id="not-found-title">404</h1>
        <p>
          This page is not part of the current world. Return to the main landing
          page or enter the immersive application.
        </p>
        <div className="not-found-actions">
          <Link to="/" className="not-found-link is-primary">
            <Home className="h-4 w-4" />
            Main Landing
          </Link>
          <Link to="/app" className="not-found-link">
            Immersive App
          </Link>
        </div>
      </section>
    </main>
  );
}

function HomePage() {
  useEffect(() => {
    validateStaticContent({ chapters: chaptersData, codexEntries: codexData });

    // Initialize sound manager on first user interaction
    const initSound = () => {
      soundManager.init();
      document.removeEventListener("click", initSound);
    };
    document.addEventListener("click", initSound);

    return () => {
      document.removeEventListener("click", initSound);
    };
  }, []);

  return (
    <div className="relative">
      <HeroSection heroData={HERO_DATA} />
      <LivingCodex />
      <ChapterSection chapters={chaptersData} />

      <SoundToggle />
    </div>
  );
}

function CodexPage() {
  return (
    <div className="relative">
      <BackHomeButton />
      <LivingCodex linkTitle={false} />
    </div>
  );
}

function ChapterPage() {
  const { chapterNumber } = useParams();
  const chapter = chaptersData.find(
    (item) => String(item.chapter_number) === String(chapterNumber),
  );

  if (!chapter) {
    return <NotFoundPage />;
  }

  return (
    <div className="relative">
      <BackHomeButton />
      <ChapterSection chapters={[chapter]} linkTitles={false} />
    </div>
  );
}

function BookChapterPage() {
  const { chapterNumber } = useParams();
  const chapter = bookData.chapters?.find(
    (item) => String(item.chapterNumber) === String(chapterNumber),
  );

  if (!chapter) {
    return <NotFoundPage />;
  }

  return <BookLanding bookData={bookData} chapter={chapter} />;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SeoManager />
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<OathboundLanding />} />
          <Route path="/purchase" element={<PurchasePage />} />
          <Route
            path="/purchase/receipt"
            element={<PurchaseReceiptPage />}
          />
          <Route path="/review" element={<ReviewPage />} />
          {LOCAL_ADMIN_ENABLED && (
            <Route path="/admin" element={<AdminConsole />} />
          )}
          {RECEIPT_PREVIEW_OUTCOMES && (
            <>
              <Route
                path="/purchase/verification-preview"
                element={<PurchaseVerification />}
              />
              <Route
                path="/purchase/receipt-preview/success"
                element={
                  <PurchaseReceiptPage
                    previewOutcome={RECEIPT_PREVIEW_OUTCOMES.success}
                  />
                }
              />
              <Route
                path="/purchase/receipt-preview/failure"
                element={
                  <PurchaseReceiptPage
                    previewOutcome={RECEIPT_PREVIEW_OUTCOMES.failure}
                  />
                }
              />
            </>
          )}
          <Route
            path="/archive"
            element={<MainLanding landingData={landingData} />}
          />
          <Route path="/author" element={<AuthorPage authorData={authorData} />} />
          <Route
            path="/lore_vault"
            element={<LoreVaultPage loreVaultData={loreVaultData} />}
          />
          <Route
            path="/lore-vault"
            element={<Navigate to="/lore_vault" replace />}
          />
          <Route path="/app" element={<HomePage />} />
          <Route path="/book" element={<BookLanding bookData={bookData} />} />
          <Route path="/book/:chapterNumber" element={<BookChapterPage />} />
          <Route path="/app/codex" element={<CodexPage />} />
          <Route path="/app/chapter/:chapterNumber" element={<ChapterPage />} />
          <Route path="/codex" element={<Navigate to="/app/codex" replace />} />
          <Route
            path="/chapter/:chapterNumber"
            element={<LegacyChapterRedirect />}
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Analytics />
        <SpeedInsights />
      </BrowserRouter>
    </div>
  );
}

function LegacyChapterRedirect() {
  const { chapterNumber } = useParams();
  return <Navigate to={`/app/chapter/${chapterNumber}`} replace />;
}

export default App;
